import type { ReactNode } from 'react';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVerticalIcon } from 'lucide-react';

import { cn } from '@mds/shared/lib/utils';

/** The lists only reorder, so an item never drifts sideways while it is dragged. */
const verticalOnly: Modifier = ({ transform }) => ({ ...transform, x: 0 });

/**
 * A vertical list reordered by dragging each item's handle — with a pointer, or from the keyboard:
 * focus the handle, Space to pick up, the arrow keys to move, Space to drop, Escape to cancel.
 * Render each item as a `SortableItem` keyed by the same id.
 */
export function SortableList<T>({
  items,
  idOf,
  nameOf,
  onReorder,
  children,
}: {
  items: T[];
  idOf: (item: T) => string;
  /** What a screen reader calls an item while it moves: "Question 3", "Section 2 · About you". */
  nameOf: (item: T, index: number) => string;
  onReorder: (next: T[]) => void;
  children: ReactNode;
}) {
  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so a click on the handle stays a click.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = items.map(idOf);
  const name = (id: string | number) => {
    const at = ids.indexOf(String(id));
    return at < 0 ? 'Item' : nameOf(items[at] as T, at);
  };
  const position = (id: string | number | undefined) =>
    `position ${ids.indexOf(String(id)) + 1} of ${ids.length}`;

  const announcements: Announcements = {
    onDragStart: ({ active }) => `Picked up ${name(active.id)}, ${position(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over ? `${name(active.id)} moved to ${position(over.id)}.` : undefined,
    onDragEnd: ({ active, over }) =>
      over ? `${name(active.id)} dropped at ${position(over.id)}.` : `${name(active.id)} dropped.`,
    onDragCancel: ({ active }) => `Moving ${name(active.id)} was cancelled.`,
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from >= 0 && to >= 0) onReorder(arrayMove(items, from, to));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[verticalOnly]}
      onDragEnd={onDragEnd}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable:
            'To reorder, press Space to pick this up, the arrow keys to move it, Space again to drop it, or Escape to cancel.',
        },
      }}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

/**
 * One item of a `SortableList`. Draws the wrapper that moves; `children` gets the drag handle to
 * place wherever the item's controls sit. With `disabled` there is no handle and nothing moves.
 */
export function SortableItem({
  id,
  label,
  disabled = false,
  className,
  children,
}: {
  id: string;
  /** The handle's accessible name: "Drag to reorder question 3". */
  label: string;
  disabled?: boolean;
  className?: string;
  children: (handle: ReactNode) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const handle = disabled ? null : (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-(--target-min) shrink-0 touch-none items-center justify-center rounded-button border border-hairline bg-surface text-ink-muted transition-colors duration-(--duration-fast) ease-standard hover:border-brand-edge hover:text-brand-text focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)',
        isDragging ? 'cursor-grabbing' : 'cursor-grab',
      )}
    >
      <GripVerticalIcon aria-hidden="true" className="size-5" />
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(className, isDragging && 'relative z-10 shadow-lift')}
    >
      {children(handle)}
    </div>
  );
}
